import { useLang } from '../i18n/lang';
import type { Strings } from '../i18n/strings';
import type { TourStop } from './tour-script';

/*
 * THE NARRATION — one short line at a time, low on the screen, while the camera flies.
 *
 * Untouchable except for the way out: the thing worth looking at is behind the words, and every
 * line is about something on screen at the moment it is said. Nothing here asks for a decision.
 *
 * IT CANNOT INVENT. A game's opening cinematic can describe a kingdom, because the kingdom is
 * whatever it says it is. Every line here has to be true of the build the reader is looking at, so
 * each is either counted from what shipped or a fact about how the map is built. That is a tighter
 * constraint and a better script — "at the centre, the names of God; every tradition puts something
 * different there" is a stronger tease than anything that could have been made up.
 *
 * TEXT, NOT A VOICE. The obvious reading of "a narrator" is recorded speech, and there is none —
 * it would have to exist in English, Hebrew and Arabic, and be re-recorded whenever a line changed.
 * Lines fading in over the flight is the version that ships and the version that translates.
 */

/** Each beat's line, and the quieter line under it. */
const SAID: Record<TourStop['says'], { line: keyof Strings; sub: keyof Strings }> = {
  size: { line: 'tour.size', sub: 'tour.size.sub' },
  centre: { line: 'tour.centre', sub: 'tour.centre.sub' },
  rim: { line: 'tour.rim', sub: 'tour.rim.sub' },
  place: { line: 'tour.place', sub: 'tour.place.sub' },
  landing: { line: 'tour.landing', sub: 'tour.landing.sub' },
};

export interface TourProps {
  /** The beat in force, or null before the first. */
  stop: TourStop | null;
  /** How many figures this build carries, counted from what ships. */
  names: number;
  /** And how many passages across every text: `bundle.meta.total`. */
  passages: number;
  /** How many scriptures, and how many traditions hold them. Both counted, never typed in prose. */
  texts: number;
  traditions: number;
  /** Leave. Always available, from the first second. */
  onSkip: () => void;
}

export function Tour({ stop, names, passages, texts, traditions, onSkip }: TourProps): React.JSX.Element | null {
  const { t, lang } = useLang();
  if (!stop) return null;
  const said = SAID[stop.says];

  /* Grouped in the reader's own language: 41,530 in English is 41.530 in some locales and reads as
     forty-one in others. */
  const group = (n: number): string => n.toLocaleString(lang);
  const params = {
    names: group(names),
    passages: group(passages),
    texts: group(texts),
    traditions: group(traditions),
    /* A beat that is not about a place still has to fill these, or `t` leaves the braces showing. */
    place: stop.place?.name ?? '',
    reach: group(stop.place?.reach ?? 0),
  };

  return (
    <>
      <div className="tour" aria-live="polite">
        <p className="tour-line">{t(said.line, params)}</p>
        <p className="tour-sub">{t(said.sub, params)}</p>
      </div>
      {/* From the first second. A reader who has met this before, or who simply wants the field,
          must not be held in a flight they did not ask for. */}
      <button type="button" className="tour-skip" onClick={onSkip}>{t('tour.skip')}</button>
    </>
  );
}
